import { Scene } from 'phaser';
import { BotaoArcade } from '../components/BotaoArcade';
import { CartaoOpcao } from '../components/CartaoOpcao';
import { desenharPaginaLima } from '../components/cenario';
import { ModalArcade } from '../components/ModalArcade';
import { criarTextoRico } from '../components/TextoRico';
import { SCENES, TEX } from '../config/constants';
import { ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { deslocamentoDasOpcoes, formatarTempo, nomeCurto, opcaoCorreta, ProgressoLabPixels, roteiro } from '../services/labPixels';
import { encaixarTexto } from '../services/scanPixelLayout';
import { Sessao } from '../services/Sessao';
import { t } from '../services/textos';

/** Quanto o resultado da bônus fica na tela dos que não responderam, antes de seguir o sorteado. */
const ESPERA_DO_RESULTADO_DO_BONUS_MS = 4000;
/** De quanto em quanto tempo a conclusão tenta de novo, enquanto outro modal ocupa a tela. */
const ESPERA_PARA_CONCLUIR_MS = 500;

const CARTOES = [{ x: 62, y: 396 }, { x: 648, y: 396 }, { x: 62, y: 534 }, { x: 648, y: 534 }];
// Enunciado: começa em y 297 e termina 16 px antes das alternativas. Se não couber, as alternativas
// descem até 30 px (sobra espaço acima do Conferir) e, se ainda faltar, a fonte diminui (28 → 20 px).
const AREA_ENUNCIADO = { topo: 297, topoCartoes: CARTOES[0].y, folga: 16, maximo: 30 };
const TAMANHOS_ENUNCIADO = [28, 26, 24, 22, 20];

/**
 * Laboratório de Pixels (Figma, quadros 67–71 e 26): quatro perguntas
 * comuns, individuais e cronometradas, e uma pergunta bônus respondida por
 * um participante sorteado em nome do grupo.
 *
 * Como o Scan Pixel, a fase é offline-first: o catálogo chega com o
 * gabarito, a correção é local e o resultado sobe ao terminar as comuns.
 */
export class QuizScene extends Scene {
    constructor () {
        super(SCENES.QUIZ);
    }

    async create () {
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        this.fonte = this.registry.get('fonte');
        this.progresso = new ProgressoLabPixels(Sessao.atual.participanteId);
        this.tela = [];             // objetos da pergunta atual
        this.cartoes = [];
        this.modal = null;
        this.cronometro = null;
        // A cena é reaproveitada: o desfecho da bônus de uma passagem não vale para a seguinte.
        this.esperandoBonus = false;
        this.respondendoBonus = undefined;
        this.conferidoPendente = null;
        this.bonusRevelado = false;
        this.conclusaoAberta = false;

        desenharPaginaLima(this, {
            titulo: t('lab.titulo'), rotuloJogador: Sessao.rotulo, rotuloY: 128, espacamento: 0
        });
        this.add.image(64, 48, TEX.BTN_INSIGHTS_REDONDO).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => !this.modal && this.scene.start(SCENES.AGUARDE, {}));
        this.add.rectangle(62, 220, 1156, 1, ARCADE_HEX.TINTA).setOrigin(0, 0.5);

        let atividades;
        try {
            atividades = await this.fonte.obterCatalogoLabPixels();
        } catch (falha) {
            console.error('[LabPixels] não foi possível carregar as perguntas.', falha);
            if (this.scene.isActive()) {
                this.add.text(640, 400, t('lab.erro_carregar'), ARCADE_TEXT.aviso).setOrigin(0.5);
                new BotaoArcade(this, 640, 500, { rotulo: t('geral.voltar'), aoClicar: () => this.scene.start(SCENES.AGUARDE, {}) });
            }
            return;
        }
        if (!this.scene.isActive()) return;

        const { comuns, bonus } = roteiro(atividades);
        this.comuns = comuns;
        this.bonus = bonus;

        if (this.progresso.estado.concluido) {
            if (!Sessao.ehTeste) {
                this.scene.start(SCENES.LAB_RESULTADO);
                return;
            }
            this.progresso.reiniciar();
        }
        this.progresso.iniciar();
        // Avisa o painel que este participante entrou na fase (zero também é aviso): o grupo
        // só espera por quem está jogando. Recarregando o app no meio, vale o que já respondeu.
        this.fonte.registrarProgresso?.('labpixels', this.progresso.respondidas);

        const cancelar = this.fonte.aoBonusLab((evento) => this.aoEventoDoBonus(evento));
        this.events.once('shutdown', cancelar);

        this.retomar();
    }

    /** Decide a tela a partir do progresso salvo (vale também depois de recarregar). */
    retomar () {
        const { estado } = this.progresso;
        if (estado.indice < this.comuns.length) return this.mostrarComum();
        if (!estado.fim) return this.fecharComuns();
        if (estado.bonus.liberado && !estado.bonus.resposta && this.bonus) return this.abrirSorteio();
        if (!estado.bonus.liberado && this.elegivelAoBonus()) return this.aguardarBonus();
        return this.abrirConclusao();
    }

    // ---------------------------------------------------------------- cabeçalho

    desenharTitulo (atividade) {
        this.titulo?.forEach((objeto) => objeto.destroy());
        const texto = this.add.text(63, 184, atividade.titulo.toUpperCase(), ARCADE_TEXT.tituloFase).setOrigin(0, 0.5);
        const icone = this.add.image(texto.x + texto.width + 10, 186, TEX.ICON_INFO_PIXEL).setOrigin(0, 0.5);
        // O "i" reabre a instrução da atividade.
        const zona = this.add.zone(63, 187, texto.width + 48, 48).setOrigin(0, 0.5)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.abrirInstrucao(atividade));
        this.titulo = [texto, icone, zona];
    }

    /** Cada pergunta comum tem o seu próprio tempo (3 min no catálogo de hoje). */
    desenharCronometro (pergunta) {
        this.pararCronometro();
        if (!pergunta.tempoSegundos) return;

        const texto = this.add.text(1215, 185, '', ARCADE_TEXT.cronometro).setOrigin(1, 0.5);
        const relogio = this.add.graphics();
        const atualizar = () => {
            const restante = this.progresso.segundosRestantes(pergunta);
            texto.setText(formatarTempo(restante));
            // Relógio: aro e dois ponteiros, à esquerda dos números.
            const cx = texto.x - texto.width - 22;
            relogio.clear().lineStyle(2.6, ARCADE_HEX.TINTA, 1).strokeCircle(cx, 188, 11)
                .lineBetween(cx, 181.5, cx, 189).lineBetween(cx, 189, cx + 6, 189);
            if (restante <= 0) this.aoEstourar();
        };
        const evento = this.time.addEvent({ delay: 250, loop: true, callback: atualizar });
        this.cronometro = { texto, relogio, evento };
        atualizar();
    }

    pararCronometro () {
        if (!this.cronometro) return;
        this.cronometro.evento.remove();
        this.cronometro.texto.destroy();
        this.cronometro.relogio.destroy();
        this.cronometro = null;
    }

    /** Tempo da pergunta esgotado: ela fica sem resposta, a seguinte abre e o bônus se perde (RN-LAB-B01). */
    aoEstourar () {
        this.progresso.estourar(this.progresso.estado.indice + 1);
        this.modal?.fechar();
        this.retomar();
    }

    // -------------------------------------------------------------- perguntas

    limparTela () {
        this.tela.forEach((objeto) => objeto.destroy());
        this.cartoes.forEach((cartao) => cartao.destroy());
        this.tela = [];
        this.cartoes = [];
        this.selecionada = null;
    }

    /** Monta rótulo, enunciado e as quatro alternativas de uma pergunta. */
    desenharPergunta (pergunta, rotulo, { tocavel }) {
        this.limparTela();
        this.pergunta = pergunta;

        this.tela.push(this.add.text(62, 265, rotulo, ARCADE_TEXT.rotuloPergunta).setOrigin(0, 0.5));
        const { topo, topoCartoes, folga, maximo } = AREA_ENUNCIADO;
        const montar = (tamanho) => criarTextoRico(this, 62, topo, 1156, pergunta.enunciado,
            { ...ARCADE_TEXT.introducao, fontSize: `${tamanho}px` }, { entrelinha: Math.round(tamanho * 34 / 28) });
        const descartar = ({ objetos }) => objetos.forEach((objeto) => objeto.destroy());
        const enunciado = encaixarTexto(montar, descartar, TAMANHOS_ENUNCIADO, topoCartoes - folga - topo + maximo);
        this.tela.push(...enunciado.objetos);
        const desce = deslocamentoDasOpcoes(enunciado.altura, AREA_ENUNCIADO);

        this.cartoes = this.progresso.opcoesDa(pergunta).slice(0, 4).map((opcao, i) => {
            const cartao = new CartaoOpcao(this, CARTOES[i].x, CARTOES[i].y + desce, {
                letra: opcao.letra, texto: opcao.texto, aoTocar: () => this.selecionar(opcao)
            });
            cartao.opcao = opcao;
            return cartao.setTocavel(tocavel);
        });
    }

    mostrarComum () {
        this.conferida = false;
        const indice = this.progresso.estado.indice;
        const pergunta = this.comuns[indice];
        const { atividade } = pergunta;

        this.desenharTitulo(atividade);
        this.desenharPergunta(pergunta, t('lab.pergunta', { numero: indice + 1 }), { tocavel: true });
        this.botao?.destroy();
        this.botao = new BotaoArcade(this, 640, 732, {
            rotulo: t('geral.conferir'), largura: 318, habilitado: false, variante: 'cinzaApagado',
            aoClicar: () => this.conferirComum()
        });

        // RI-LAB-01: a atividade abre pela instrução (na primeira pergunta dela); o tempo só corre depois do Iniciar.
        const primeiraDaAtividade = this.comuns.findIndex((p) => p.atividade.id === atividade.id) === indice;
        if (primeiraDaAtividade && !this.progresso.iniciouPrazo(pergunta) && atividade.abertura) {
            this.abrirInstrucao(atividade, () => this.desenharCronometro(pergunta));
        } else {
            this.desenharCronometro(pergunta);
        }
    }

    /** RI-LAB-03: Conferir só habilita com uma alternativa selecionada. */
    selecionar (opcao) {
        if (this.modal || this.conferida) return;
        this.selecionada = opcao;
        this.cartoes.forEach((cartao) => cartao.setEstado(cartao.opcao === opcao ? 'selecionada' : 'normal'));
        this.botao?.setHabilitado(true);
        if (this.respondendoBonus) this.fonte.publicarBonusLab({ tipo: 'selecao', opcaoId: opcao.id });
    }

    /** RI-LAB-04: erro em vermelho, e a alternativa correta sempre identificada em verde. */
    revelar (escolhidaId) {
        this.conferida = true;
        const certa = opcaoCorreta(this.pergunta);
        this.cartoes.forEach((cartao) => {
            cartao.setTocavel(false);
            if (cartao.opcao.id === certa?.id) cartao.setEstado('correta');
            else if (cartao.opcao.id === escolhidaId) cartao.setEstado('errada');
            else cartao.setEstado('normal');
        });
    }

    conferirComum () {
        if (!this.selecionada || this.conferida) return;
        this.progresso.responder(this.pergunta, this.selecionada);
        this.revelar(this.selecionada.id);

        // RI-LAB-05: Continuar leva à pergunta seguinte.
        this.botao.destroy();
        this.botao = new BotaoArcade(this, 640, 732, {
            rotulo: t('geral.continuar'), largura: 318, aoClicar: () => {
                // Só aqui a pergunta conta como concluída para o painel: quem ainda está vendo o resultado
                // da última não fecha a etapa do grupo (nem libera a bônus). Não é `pergunta.ordem`: a ordem
                // recomeça em cada atividade e o painel nunca chegaria ao total.
                this.fonte.registrarProgresso?.('labpixels', this.progresso.respondidas);
                this.conferida = false;
                this.retomar();
            }
        });
    }

    // ------------------------------------------------------------ fim das comuns

    /** RN-LAB-06: a conclusão individual é registrada e enviada sem esperar o grupo. */
    fecharComuns () {
        this.pararCronometro();
        this.progresso.fecharComuns();
        this.enviarIndividual();
        this.retomar();
    }

    async enviarIndividual () {
        if (this.progresso.estado.enviado) return;
        try {
            await this.fonte.enviarResultadoLabPixels(this.progresso.envio());
            this.progresso.marcarEnviado();
        } catch (falha) {
            // Fica pendente; a tela de resultados tenta de novo com a mesma chave.
            console.error('[LabPixels] resultado não enviado; fica pendente.', falha);
        }
    }

    /** RN-LAB-B01: só há bônus para quem terminou as comuns dentro do tempo. */
    elegivelAoBonus () {
        return Boolean(this.bonus) && !this.progresso.estado.estourou;
    }

    // ------------------------------------------------------------------ bônus

    /** RN-LAB-B02: mesmo elegível, o grupo espera a liberação do facilitador. */
    aguardarBonus () {
        this.limparTela();
        this.botao?.destroy();
        this.botao = null;
        this.tela.push(
            this.add.text(640, 400, t('lab.respostas_registradas'), { ...ARCADE_TEXT.manchete, align: 'center' }).setOrigin(0.5),
            this.add.text(640, 462, t('lab.espera_bonus'), ARCADE_TEXT.aviso).setOrigin(0.5)
        );
        this.esperandoBonus = true;
        this.fonte.solicitarBonusLab();
    }

    aoEventoDoBonus (evento) {
        if (!this.scene.isActive()) return;

        if (evento.tipo === 'liberado' && this.esperandoBonus) {
            this.esperandoBonus = false;
            this.progresso.liberarBonus({ sorteado: evento.sorteado, semente: evento.semente });   // RN-LAB-B03
            this.abrirSorteio();
        } else if (evento.tipo === 'indisponivel' && this.esperandoBonus) {
            this.esperandoBonus = false;
            this.abrirConclusao();
        } else if (this.respondendoBonus === false && this.pergunta === this.bonus) {
            // RI-LAB-B02: nos demais tablets, a escolha do sorteado aparece em tempo real.
            if (evento.tipo === 'selecao') {
                this.cartoes.forEach((c) => c.setEstado(c.opcao.id === evento.opcaoId ? 'selecionada' : 'normal'));
            } else if (evento.tipo === 'conferido') {
                this.aplicarConferencia(evento.opcaoId);
            }
        } else if (evento.tipo === 'conferido' && this.respondendoBonus === undefined && this.bonus) {
            // O sorteado conferiu antes de este tablet sair do modal do sorteio: o evento é único e não
            // se repete, então fica guardado para a pergunta da bônus abrir já com o desfecho.
            this.conferidoPendente = evento.opcaoId;
        }
    }

    /** O sorteado conferiu: mostra o desfecho, guarda o acerto do grupo e segue para a conclusão. Vale uma vez. */
    aplicarConferencia (opcaoId) {
        if (this.bonusRevelado) return;
        this.bonusRevelado = true;
        this.conferidoPendente = null;
        this.revelar(opcaoId);
        // A bônus é do grupo: quem não é o sorteado guarda se acertaram, para o card de pontos do grupo.
        this.progresso.registrarBonusDoGrupo(opcaoCorreta(this.pergunta)?.id === opcaoId);
        this.acompanharConclusaoDoBonus();
    }

    souOSorteado () {
        return this.progresso.estado.bonus.sorteado?.id === Sessao.atual.participanteId;
    }

    abrirSorteio () {
        const { sorteado } = this.progresso.estado.bonus;
        this.limparTela();
        this.modal = new ModalArcade(this, {
            titulo: t('lab.titulo'),
            largura: 800,
            blocos: [
                { texto: t('lab.bonus_sorteio'), peso: '600', maiusculas: true },
                { gigante: nomeCurto(sorteado?.nome) || t('lab.bonus_participante') }
            ],
            botao: { rotulo: t('geral.iniciar'), largura: 404, aoClicar: () => this.mostrarBonus() },
            aoFechar: () => { this.modal = null; }
        });
    }

    mostrarBonus () {
        // RN-LAB-B04 / B05: só o sorteado responde; os demais acompanham.
        this.respondendoBonus = this.souOSorteado();
        this.conferida = false;
        this.bonusRevelado = false;
        this.desenharPergunta(this.bonus, t('lab.pergunta_bonus'), { tocavel: this.respondendoBonus });

        // RI-LAB-B01: indicação clara de quem é o responsável pela resposta.
        const { sorteado } = this.progresso.estado.bonus;
        this.tela.push(this.add.text(1218, 265,
            this.respondendoBonus ? t('lab.bonus_voce') : t('lab.bonus_quem', { nome: nomeCurto(sorteado?.nome) }),
            { ...ARCADE_TEXT.rotuloCodigo, fontSize: '16px', letterSpacing: -1.2 }).setOrigin(1, 0.5));

        this.botao?.destroy();
        this.botao = null;
        // RI-LAB-B03: nos tablets de quem não foi sorteado, não há botão Conferir.
        if (this.respondendoBonus) {
            this.botao = new BotaoArcade(this, 640, 732, {
                rotulo: t('geral.conferir'), largura: 318, habilitado: false, variante: 'cinzaApagado',
                aoClicar: () => this.conferirBonus()
            });
            return;
        }
        // Sem botão, só o evento tira esta tela. Se ele já passou (modal do sorteio aberto, tablet fora do
        // ar), vem do que ficou guardado ou da releitura do estado do grupo.
        if (this.conferidoPendente != null) this.aplicarConferencia(this.conferidoPendente);
        else this.fonte.solicitarBonusLab();
    }

    conferirBonus () {
        if (!this.selecionada || this.conferida) return;
        this.progresso.responderBonus(this.bonus, this.selecionada);
        this.revelar(this.selecionada.id);
        this.fonte.publicarBonusLab({ tipo: 'conferido', opcaoId: this.selecionada.id });
        this.enviarGrupo();
        this.mostrarContinuarDoBonus();
    }

    async enviarGrupo () {
        try {
            await this.fonte.enviarResultadoGrupoLabPixels(this.progresso.envioDoGrupo());
            this.progresso.marcarGrupoEnviado();
        } catch (falha) {
            console.error('[LabPixels] resultado do grupo não enviado; fica pendente.', falha);
        }
    }

    /**
     * Quem não foi sorteado não tem botão: o fluxo segue o do sorteado. Conferida a resposta, o desfecho é
     * guardado para a linha Bônus do placar e, depois de um instante para ler o resultado, a conclusão abre.
     */
    acompanharConclusaoDoBonus () {
        if (!this.progresso.estado.bonus.resposta) {
            const escolhida = this.cartoes.find((c) => c.estado === 'errada') ?? this.cartoes.find((c) => c.estado === 'correta');
            if (escolhida) this.progresso.responderBonus(this.bonus, escolhida.opcao);
        }
        this.botao?.destroy();
        this.botao = null;
        // Com outro modal aberto (o "i" da instrução), `abrirConclusao` recusa: tenta de novo quando fechar.
        const concluir = () => {
            if (!this.scene.isActive() || this.conclusaoAberta) return;
            if (this.modal) this.time.delayedCall(ESPERA_PARA_CONCLUIR_MS, concluir);
            else this.abrirConclusao();
        };
        this.time.delayedCall(ESPERA_DO_RESULTADO_DO_BONUS_MS, concluir);
    }

    mostrarContinuarDoBonus () {
        this.botao?.destroy();
        this.botao = new BotaoArcade(this, 640, 732, {
            rotulo: t('geral.continuar'), largura: 318, aoClicar: () => this.abrirConclusao()
        });
    }

    // ------------------------------------------------------------------ modais

    abrirInstrucao (atividade, aoIniciar = null) {
        if (this.modal) return;
        if (!atividade.abertura) {
            aoIniciar?.();
            return;
        }
        const [primeira, ...demais] = atividade.abertura.match(/[^.!?]+[.!?]+(\s|$)/g)?.map((f) => f.trim())
            ?? [atividade.abertura];

        this.modal = new ModalArcade(this, {
            titulo: t('lab.titulo'),
            largura: 740,
            blocos: [{ texto: primeira, peso: '600', maiusculas: true }, ...demais.map((texto) => ({ texto }))],
            // RI-LAB-02: Iniciar leva à primeira pergunta.
            botao: { rotulo: t('geral.iniciar'), aoClicar: aoIniciar },
            aoFechar: () => { this.modal = null; }
        });
    }

    /** RI-LAB-06 / RI-LAB-07. */
    abrirConclusao () {
        if (this.modal) return;
        this.conclusaoAberta = true;
        this.pararCronometro();
        this.limparTela();
        this.botao?.destroy();
        this.botao = null;

        this.modal = new ModalArcade(this, {
            titulo: t('lab.titulo'),
            largura: 760,
            blocos: [
                { texto: t('lab.conclusao_manchete'), peso: '600', maiusculas: true },
                { texto: t('lab.conclusao_texto_1') },
                { texto: t('lab.conclusao_texto_2') }
            ],
            botao: {
                rotulo: t('lab.conferir_pontuacao'), largura: 404,
                aoClicar: () => {
                    this.progresso.concluir();
                    this.scene.start(SCENES.LAB_RESULTADO);
                }
            },
            aoFechar: () => { this.modal = null; }
        });
    }
}
